import { useEffect, useState } from 'react';

import type { ContentImportRun, ImportRunChannelData } from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Badge, LoadingIndicator, toast } from '@gitbook/spine-react';
import { usePrevious } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import { apiClient, useCurrentOrganizationId } from '../../../state';
import {
    PageGroupHighlight,
    PageGroupHighlightHeader,
    PageGroupHighlightLoadingState,
} from './PageGroupHighlight';

export function ImportPagesGroup(props: {
    children: React.ReactNode;
    importRun: ContentImportRun;
    isEmpty: boolean;
}) {
    const { importRun } = props;
    const organizationId = useCurrentOrganizationId();
    assert(organizationId, 'Organization ID is required');
    const { status } = importRun;
    const previousStatus = usePrevious(status);

    const [justCompleted, setJustCompleted] = useState(false);

    useEffect(() => {
        if (justCompleted) {
            const timeout = setTimeout(() => {
                setJustCompleted(false);
            }, 3000);
            return () => clearTimeout(timeout);
        }
    }, [justCompleted]);

    useEffect(() => {
        if (
            (previousStatus === 'in-progress' || previousStatus === 'pending') &&
            status === 'completed'
        ) {
            setJustCompleted(true);
        }
    }, [previousStatus, status]);

    const descriptor = status ? getImportDescriptor(status) : null;

    return (
        <PageGroupHighlight
            isDisabled={!status || (status === 'completed' && !justCompleted)}
            isActive={status === 'pending' || status === 'in-progress' || justCompleted}
        >
            {descriptor && (
                <PageGroupHighlightHeader>
                    {status === 'in-progress' || status === 'pending' ? (
                        <PaletteButton
                            kind="pill"
                            size="xsmall"
                            label={descriptor.label}
                            leadingIcon={descriptor.icon}
                            palette={() => {
                                return createPalette({
                                    groups: [
                                        {
                                            key: 'default',
                                            commands: [
                                                {
                                                    key: 'stop',
                                                    title: 'Stop',
                                                    icon: <Icon.X />,
                                                    trigger: () => {
                                                        toast.promise(
                                                            apiClient.imports.cancelImportRun(
                                                                organizationId,
                                                                importRun.id
                                                            ),
                                                            {
                                                                loading: 'Stopping import…',
                                                                success: 'Import stopped',
                                                            }
                                                        );
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                });
                            }}
                        />
                    ) : (
                        <Badge
                            kind={status === 'completed' ? 'done' : 'secondary'}
                            size="xsmall"
                            label={descriptor.label}
                            icon={descriptor.icon}
                        />
                    )}
                </PageGroupHighlightHeader>
            )}
            {props.isEmpty ? <PageGroupHighlightLoadingState /> : props.children}
        </PageGroupHighlight>
    );
}

function getImportDescriptor(status: ImportRunChannelData['data']['status']) {
    switch (status) {
        case 'pending':
        case 'in-progress':
            return {
                label: 'Importing…',
                icon: <LoadingIndicator />,
            };
        case 'completed':
            return {
                label: 'Complete',
                icon: <Icon.CheckCircle />,
            };
        case 'failed':
            return {
                label: 'Import failed',
                icon: <Icon.X />,
            };
        case 'cancelled':
            return {
                label: 'Import cancelled',
                icon: <Icon.X />,
            };
        default:
            assertNever(status);
    }
}
